import { ReactNode, useCallback, useEffect } from 'react';
import { Alert } from 'antd';
import * as Icon from 'react-feather';
import styled from 'styled-components';
import {
  colorSuccess,
  colorError,
  colorWarning,
} from '@atlas-frontend/design-tokens';
import { useAlertContext } from './useAlertContext';

const alertIcons: Record<'success' | 'warning' | 'error', ReactNode> = {
  success: <Icon.Check />,
  warning: <Icon.AlertTriangle />,
  error: <Icon.AlertTriangle />,
};

const alertBorders = {
  success: colorSuccess,
  warning: colorWarning,
  error: colorError,
};

export const AlertToast = () => {
  const { alert, resetAlert } = useAlertContext();
  const timeout = alert?.timeout ?? 5000;

  // clean up alert on unmount
  useEffect(() => () => resetAlert(), [resetAlert]);

  const handleClose = useCallback(() => {
    resetAlert();
  }, [resetAlert]);

  useEffect(() => {
    if (!alert || !timeout) return;
    const timer = setTimeout(handleClose, timeout);
    return () => clearTimeout(timer);
  }, [handleClose, alert, timeout]);

  if (!alert) return null;

  const icon = alertIcons[alert.type];
  const border = alertBorders[alert.type];

  return (
    <Wrapper>
      <StyledAlert
        $border={border}
        type={alert.type}
        message={alert.message}
        description={alert.description}
        icon={icon}
        closable
        banner
        onClose={handleClose}
      />
      {timeout && (
        <LoadingBar key={alert.message} $timeout={timeout} $color={border} />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: fixed;
  top: 75px;
  right: 15px;
  min-width: 350px;
  max-width: 90%;
  opacity: 0.9;
  z-index: 9999;
`;

const StyledAlert = styled(Alert)<{ $border: string }>`
  border-radius: 0;
  border-color: ${(p) => p.$border};
`;

const LoadingBar = styled.div<{ $timeout?: number; $color: string }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  background-color: ${(p) => p.$color};
  animation: shrink ${(p) => p.$timeout || 0}ms linear forwards;
  @keyframes shrink {
    from {
      width: 0%;
    }
    to {
      width: 100%;
    }
  }
`;
